import api from './api';

/**
 * تحويل مفتاح VAPID من base64url (نص) لصيغة Uint8Array المطلوبة من المتصفح
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

/**
 * هل المتصفح الحالي يدعم إشعارات Push أصلاً؟ (بعض المتصفحات القديمة أو iOS
 * الإصدارات القديمة لا تدعم، أو لازم يكون التطبيق مثبت أولاً على iOS)
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * الحالة الحالية لإذن الإشعارات: 'granted' | 'denied' | 'default'
 */
export function getPermissionState() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * تفعيل إشعارات Push: يطلب إذن المستخدم، يشترك عبر المتصفح، ويرسل الاشتراك للسيرفر.
 * client: عميل axios المطلوب استخدامه — افتراضياً `api` (المستخدم العادي/الأدمن)،
 * أو مرّر `distributorApi` عند الاستدعاء من لوحة الموزع (يحمل توكن dist_token
 * الخاص به بدل token العادي).
 * يرجع true لو نجح التفعيل، false لو رفض المستخدم أو صار خطأ.
 */
export async function subscribeToPush(client = api) {
  if (!isPushSupported()) return false;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    const { data } = await client.get('/push/vapid-public-key');
    if (!data.publicKey) return false;

    const registration = await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
    }

    // ⚠️ إصلاح مهم: سابقاً كنا نرسل الاشتراك للسيرفر فقط لو كان جديداً (لم يكن
    // موجوداً أصلاً بالمتصفح). لكن اشتراك Push مخزَّن بالمتصفح نفسه (ثابت طالما
    // لم يُلغَ يدوياً)، بينما بيانات السيرفر أصبحت الآن مقسّمة لكل شبكة (تعدد
    // الشبكات) بقاعدة بيانات مستقلة. فمستخدم كان مشتركاً مسبقاً (قبل إضافة
    // تعدد الشبكات، أو ببساطة بعد أي مسح/تبديل لقاعدة بيانات شبكته) يملك
    // اشتراكاً صالحاً بالمتصفح، لكن لا يوجد له سجل مطابق بقاعدة بيانات الشبكة
    // الحالية — فتُتخطى خطوة الحفظ هنا بصمت ويبقى بدون إشعارات نهائياً رغم
    // ظهور كل شيء "مفعّل" من ناحيته. الحل: نُرسل الاشتراك للسيرفر دائماً (سواء
    // جديداً أو موجوداً مسبقاً بالمتصفح) — الراوت upsert بطبيعته فلا ضرر من
    // إعادة إرساله، ويضمن وجود السجل دائماً بقاعدة بيانات الشبكة الصحيحة.
    await client.post('/push/subscribe', { subscription: subscription.toJSON() });
    return true;
  } catch (e) {
    console.error('فشل تفعيل إشعارات Push:', e);
    return false;
  }
}

/**
 * إلغاء تفعيل إشعارات Push على هذا الجهاز تحديداً.
 * نفس معامل client أعلاه — مرّر distributorApi عند الاستدعاء من لوحة الموزع.
 */
export async function unsubscribeFromPush(client = api) {
  if (!isPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await client.post('/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {});
      await subscription.unsubscribe();
    }
  } catch (e) {
    console.error('فشل إلغاء إشعارات Push:', e);
  }
}

/**
 * هل الجهاز الحالي مشترك فعلياً بإشعارات Push؟
 */
export async function isSubscribed() {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (e) {
    return false;
  }
}
